'use client'

import { reportHandledException } from '@/lib/error-reporting'
import React, { useCallback, useMemo, useState } from 'react'
import { Search } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Button,
  clampPage,
  EmptyState,
  Pagination,
  SearchInput,
  SegmentedControl,
  StatusPill,
  Tag,
  useKiaoConfirm,
  toast,
} from '@kiao-voice/ui-kit'
import type { OrganizationMemberSummary, SeatUsage } from '@kiao-voice/api-client'
import { revokeMember, TeamApiError, updateMemberAccess } from './api-client'
import {
  resolveMemberAccessAction,
  resolveMemberStatus,
  type MemberAccessAction,
} from './team-billing-state'

const PAGE_SIZE = 8

type MemberFilter = 'all' | 'active' | 'invited'

export interface MembersSectionProps {
  organizationId: number
  members: OrganizationMemberSummary[]
  /** 簽入帳號的 email，用來在自己那列加上「你」標籤。 */
  currentEmail: string | null
  /** 檢視者是團隊擁有者：只有擁有者可以啟用／停用成員。 */
  viewerIsOwner: boolean
  getTokenFn: () => string | null
  onRevoked: () => void
  /** 啟用或停用成功；頁面重新讀取成員與席次。 */
  onAccessChanged: () => void
  onError: (cause: unknown) => void
}

/** 啟用時席次已滿，伺服器回的占用數；其他錯誤交給頁面處理。 */
function seatLimitUsage(cause: unknown): SeatUsage | null {
  if (!(cause instanceof TeamApiError) || cause.code !== 'seat_limit_reached') return null
  return (cause.body as { usage?: SeatUsage } | null)?.usage ?? null
}

/**
 * `03 成員`：一份成員陣列，搜尋／狀態篩選／分頁是它的三個視圖——列永遠從資料算，
 * 不從 DOM 翻。席位數不在這裡推導（它只信 `seatUsage`，見 `SeatSection`）。
 */
export function MembersSection({
  organizationId,
  members,
  currentEmail,
  viewerIsOwner,
  getTokenFn,
  onRevoked,
  onAccessChanged,
  onError,
}: MembersSectionProps) {
  const { t } = useTranslation('web')
  const confirm = useKiaoConfirm()
  const [revokingId, setRevokingId] = useState<number | null>(null)
  const [accessBusyId, setAccessBusyId] = useState<number | null>(null)
  const [seatLimit, setSeatLimit] = useState<SeatUsage | null>(null)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<MemberFilter>('all')
  const [page, setPage] = useState(0)

  const roleLabel = (role: OrganizationMemberSummary['role']) =>
    role === 'owner'
      ? t('team.roleOwner')
      : role === 'admin'
        ? t('team.roleAdmin')
        : t('team.roleMember')

  const filtered = useMemo(() => {
    const keyword = query.trim().toLowerCase()
    return members.filter((member) => {
      if (filter === 'invited' && member.status !== 'invited') return false
      if (filter === 'active' && member.status === 'invited') return false
      if (keyword && !member.email.toLowerCase().includes(keyword)) return false
      return true
    })
  }, [members, query, filter])

  const currentPage = clampPage(page, PAGE_SIZE, filtered.length)
  const pageMembers = filtered.slice(currentPage * PAGE_SIZE, (currentPage + 1) * PAGE_SIZE)

  const revoke = useCallback(
    async (member: OrganizationMemberSummary) => {
      const invited = member.status === 'invited'
      const ok = await confirm({
        title: invited ? t('team.removeConfirmTitleInvite') : t('team.removeConfirmTitleMember'),
        message: t('team.removeConfirmBody', { email: member.email }),
        confirmText: invited ? t('team.removeInviteConfirmCta') : t('team.removeConfirmCta'),
        cancelText: t('common:cancel'),
      })
      if (!ok) return
      const token = getTokenFn()
      if (!token) return
      setRevokingId(member.id)
      try {
        await revokeMember(organizationId, member.id)
        onRevoked()
      } catch (cause) {
        reportHandledException(cause, { source: 'features/team/members-section.tsx' })
        onError(cause)
      } finally {
        setRevokingId(null)
      }
    },
    [confirm, t, getTokenFn, organizationId, onRevoked, onError]
  )

  const changeAccess = useCallback(
    async (member: OrganizationMemberSummary, action: MemberAccessAction) => {
      if (action === 'disable') {
        const ok = await confirm({
          title: t('team.memberDisableConfirmTitle', { email: member.email }),
          message: t('team.memberDisableConfirmBody'),
          confirmText: t('team.memberDisableConfirmCta'),
          cancelText: t('common:cancel'),
        })
        if (!ok) return
      }
      if (!getTokenFn()) return
      setSeatLimit(null)
      setAccessBusyId(member.id)
      try {
        await updateMemberAccess(
          organizationId,
          member.id,
          action === 'disable' ? 'disabled' : 'active'
        )
        toast.success(
          t(action === 'disable' ? 'team.memberDisabledToast' : 'team.memberEnabledToast', {
            email: member.email,
          })
        )
        onAccessChanged()
      } catch (cause) {
        reportHandledException(cause, { source: 'features/team/members-section.tsx' })
        const usage = seatLimitUsage(cause)
        if (usage) setSeatLimit(usage)
        else onError(cause)
      } finally {
        setAccessBusyId(null)
      }
    },
    [confirm, t, getTokenFn, organizationId, onAccessChanged, onError]
  )

  return (
    <>
      {seatLimit ? (
        <Alert tone="warn" className="mb-[var(--gap-sm)]" data-testid="member-access-seat-limit">
          {t('team.errMemberEnableSeatLimit', { used: seatLimit.used, total: seatLimit.total })}{' '}
          <a className="link" href="#team-seats">
            {t('team.errMemberEnableSeatLimitCta')}
          </a>
        </Alert>
      ) : null}
      <div className="mb-[var(--gap-sm)] flex flex-wrap items-center gap-[var(--gap-sm)]">
        <div className="min-w-[200px] basis-[260px]">
          <SearchInput
            value={query}
            placeholder={t('team.membersSearchPlaceholder')}
            aria-label={t('team.membersSearchAria')}
            data-testid="members-search"
            onChange={(event) => {
              setQuery(event.target.value)
              setPage(0)
            }}
            onClear={() => {
              setQuery('')
              setPage(0)
            }}
          />
        </div>
        <SegmentedControl
          variant="pill"
          aria-label={t('team.membersFilterAria')}
          value={filter}
          options={[
            { value: 'all' as const, label: t('team.membersFilterAll') },
            { value: 'active' as const, label: t('team.statusActive') },
            { value: 'invited' as const, label: t('team.statusInvited') },
          ]}
          onValueChange={(value) => {
            setFilter(value)
            setPage(0)
          }}
        />
      </div>

      {filtered.length === 0 ? (
        <EmptyState
          joined
          icon={<Search aria-hidden="true" />}
          title={t('team.membersEmptyTitle')}
          description={
            query.trim()
              ? t('team.membersEmptyQueryDesc', { query: query.trim() })
              : t('team.membersEmptyStatusDesc')
          }
          action={
            <Button
              variant="secondary"
              onClick={() => {
                setQuery('')
                setFilter('all')
                setPage(0)
              }}
            >
              {t('team.membersClearFilters')}
            </Button>
          }
        />
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="tbl">
              <thead>
                <tr>
                  <th>{t('team.colMember')}</th>
                  <th>{t('team.colRole')}</th>
                  <th>{t('team.colStatus')}</th>
                  <th className="td-act">{t('team.colActions')}</th>
                </tr>
              </thead>
              <tbody>
                {pageMembers.map((m) => {
                  const invited = m.status === 'invited'
                  const status = resolveMemberStatus(m)
                  const accessAction = resolveMemberAccessAction(m, viewerIsOwner)
                  return (
                    <tr key={m.id}>
                      <td>
                        <span className="inline-flex flex-wrap items-center gap-[var(--gap-xs)]">
                          {m.email}
                          {currentEmail && m.email === currentEmail ? (
                            <Tag>{t('team.youTag')}</Tag>
                          ) : null}
                        </span>
                      </td>
                      <td>{roleLabel(m.role)}</td>
                      <td>
                        <StatusPill tone={status.tone}>{t(status.key)}</StatusPill>
                      </td>
                      <td className="td-act">
                        {accessAction ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={accessBusyId === m.id}
                            onClick={() => void changeAccess(m, accessAction)}
                          >
                            {accessAction === 'disable'
                              ? t('team.memberDisable')
                              : t('team.memberEnable')}
                          </Button>
                        ) : null}
                        {m.role !== 'owner' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={revokingId === m.id}
                            onClick={() => void revoke(m)}
                          >
                            {invited ? t('team.removeInviteCta') : t('team.removeFromTeam')}
                          </Button>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {/* `Pagination` 只在 `total === 0` 自己回 null，所以單頁的情況由這裡擋：
              一頁就放得下時顯示「顯示 1–2 筆，共 2 筆」＋兩顆停用的按鈕是雜訊。 */}
          {filtered.length > PAGE_SIZE ? (
            <Pagination
              page={currentPage}
              pageSize={PAGE_SIZE}
              total={filtered.length}
              onPageChange={setPage}
              labels={{
                range: ({ start, end, total }) => t('team.membersPageRange', { start, end, total }),
                previous: t('team.membersPagePrev'),
                next: t('team.membersPageNext'),
              }}
            />
          ) : null}
        </>
      )}
    </>
  )
}
